import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import api from '../../lib/api';

const LOGO_URL = "/assets/sios-logo.png";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [ctaButton, setCtaButton] = useState(null);
  const [eventChallenges, setEventChallenges] = useState([]);
  const location = useLocation();

  useEffect(() => {
    const loadNavData = async () => {
      try {
        const res = await api.get('/settings/public');
        if (res.data?.navbar_cta_enabled && res.data?.navbar_cta_text) {
          setCtaButton({
            text: res.data.navbar_cta_text,
            url: res.data.navbar_cta_url || '#',
            color: res.data.navbar_cta_color || '#7B2CBF'
          });
        }
      } catch (err) {
        console.error('CTA button config load failed:', err);
      }
    };
    loadNavData();
  }, []);

  // When on an event page, fetch its (parent) challenges and inject as nav links
  useEffect(() => {
    const m = location.pathname.match(/^\/events\/([^/]+)/);
    if (!m) { setEventChallenges([]); return; }
    const slug = m[1];
    let cancelled = false;
    (async () => {
      try {
        const evRes = await api.get(`/events/${slug}`);
        if (cancelled || !evRes.data?.id) return;
        const chRes = await api.get('/challenges', { params: { event_id: evRes.data.id, public_only: true } });
        if (cancelled) return;
        setEventChallenges((chRes.data || []).filter(c => !c.parent_challenge_id));
      } catch {
        if (!cancelled) setEventChallenges([]);
      }
    })();
    return () => { cancelled = true; };
  }, [location.pathname]);

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/editions', label: 'Edizioni' },
    { href: '/speakers', label: 'Speaker' },
    ...eventChallenges.map(ch => ({
      href: `/challenges/${ch.slug || ch.id}`,
      label: ch.nav_label || ch.title,
    })),
    { href: '/diventa-partner', label: 'Diventa Partner' },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-sios-void/80 backdrop-blur-xl h-20" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
          <img src={LOGO_URL} alt="SIOS" className="h-10 w-auto" />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className={`text-sm font-medium tracking-wide transition-colors hover:text-primary ${
                isActive(link.href) ? 'text-primary' : 'text-muted-foreground'
              }`}
              data-testid={`nav-link-${link.label.toLowerCase()}`}
            >
              {link.label}
            </Link>
          ))}
          
          {/* CTA Button */}
          {ctaButton && (
            <a 
              href={ctaButton.url}
              target="_blank"
              rel="noopener noreferrer"
              className="px-5 py-2 rounded-full font-medium text-white text-sm transition-all hover:opacity-90 hover:scale-105 shadow-lg"
              style={{ backgroundColor: ctaButton.color }}
              data-testid="navbar-cta-button"
            >
              {ctaButton.text}
            </a>
          )}
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-foreground"
          onClick={() => setIsOpen(!isOpen)}
          data-testid="mobile-menu-btn"
        >
          {isOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Navigation */}
      {isOpen && (
        <div className="md:hidden glass-heavy border-t border-white/10 p-6" data-testid="mobile-menu">
          <div className="flex flex-col gap-4">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-lg font-medium ${
                  isActive(link.href) ? 'text-primary' : 'text-muted-foreground'
                }`}
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            
            {/* Mobile CTA Button */}
            {ctaButton && (
              <a 
                href={ctaButton.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 px-5 py-3 rounded-full font-medium text-white text-center transition-all"
                style={{ backgroundColor: ctaButton.color }}
                onClick={() => setIsOpen(false)}
              >
                {ctaButton.text}
              </a>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};
